import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';

import { toast } from 'sonner';
import { toNoonUTC } from '@/shared/lib/utils';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { shortFormIntakeApi } from '@client/api/waterlily';

import type { QuickPlanFormValues, QuickPlanPersonValues } from '@/features/client-quick-plan';

interface UseShortFormIntakeOptions {
  advisorId: string;
  onSuccess?: (data: ShortFormIntakeResponse) => void;
  /**
   * Whether to also email the client the full questionnaire. The only thing separating the two
   * New Case Quick Start options once both collect the same details.
   */
  sendIntakeForm?: boolean;
}

function mapFormToRequest(
  formValues: QuickPlanPersonValues & { agentEnteredIntakeId?: string },
  advisorId: string,
  sendIntakeForm: boolean,
): ShortFormIntakeRequest {
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
  };

  const smokerMap: Record<string, string> = {
    never: 'No',
    former: 'Yes',
    current: 'Yes',
  };

  const intakeFormUrl = getIntroIntakeFormApiEndPoint(advisorId, false);

  return {
    clientFirstName: formValues.firstName,
    clientLastName: formValues.lastName,
    clientBirthDate: toNoonUTC(formValues.birthDate),
    clientGenderAtBirth: genderMap[formValues.sex] ?? 'Male',
    clientSmokerEver: smokerMap[formValues.smokingStatus] ?? 'No',
    clientMaritalStatus: formValues.maritalStatus,
    clientZipCode: formValues.zipCode,
    clientEmail: formValues.email || undefined,
    // The server sends the questionnaire whenever an email is present and this is not set. This
    // path now collects an email for the consent request, so without it the "No intake form"
    // option would email the client a form the advisor explicitly chose not to send.
    skipInviteEmail: !sendIntakeForm,
    // Present only for advisors whose program requires agent-entered consent; the server
    // refuses the request without it and ignores it for everyone else.
    agentEnteredIntakeId: formValues.agentEnteredIntakeId,
    intakeFormUrl,
  };
}

export function useShortFormIntake({
  advisorId,
  onSuccess,
  sendIntakeForm = false,
}: UseShortFormIntakeOptions) {
  const navigate = useNavigate();
  const lastCoupleLinked = useRef(true);
  const lastCoupleDeliveryFailed = useRef(false);

  const mutation = useMutation({
    mutationFn: async (formValues: QuickPlanFormValues): Promise<ShortFormIntakeResponse> => {
      const primaryRequest = mapFormToRequest(formValues, advisorId, sendIntakeForm);

      if (formValues.hasPartner && formValues.partner) {
        const partnerRequest = mapFormToRequest(formValues.partner, advisorId, sendIntakeForm);
        const coupleResponse = await shortFormIntakeApi.createCouple({
          primary: primaryRequest,
          partner: partnerRequest,
        });
        lastCoupleLinked.current = coupleResponse.linked;
        lastCoupleDeliveryFailed.current = [coupleResponse.primary, coupleResponse.partner].some(
          result => result.intakeFormDeliveryStatus === 'failed',
        );
        return coupleResponse.primary;
      }

      return shortFormIntakeApi.create(primaryRequest);
    },
    onSuccess: (data, variables) => {
      const intakeFormDeliveryFailed = variables.hasPartner
        ? lastCoupleDeliveryFailed.current
        : data.intakeFormDeliveryStatus === 'failed';

      if (sendIntakeForm && intakeFormDeliveryFailed) {
        toast.error('We couldn’t send that email', {
          description: 'Nothing reached your client. Try sending it again.',
        });
      } else if (variables.hasPartner) {
        if (lastCoupleLinked.current) {
          toast.success('Quick plan created for both clients');
        } else {
          toast.warning(
            'Quick plans created for both clients, but linking failed. Please link them manually from your dashboard.',
          );
        }
      } else {
        toast.success(
          sendIntakeForm ? 'Plan created and intake form sent' : 'Quick plan created successfully',
        );
      }

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/advisors/${advisorId}/clients`);
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create quick plan. Please try again.';
      toast.error(errorMessage);
    },
  });

  return {
    submitQuickPlan: mutation.mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
